<!-- ═══════════════════════════════════════════════════════════════════════
     Convert dropdown button
     Only shown in detail page headers (showDropdownButton=true).
     For list pages, parent calls openForRow() via @ViewChild instead.
     ═══════════════════════════════════════════════════════════════════════ -->
<div class="btn-group" *ngIf="showDropdownButton && isConvertEnabled">
    <button type="button"
            class="btn btn-sm btn-outline-secondary dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false">
        <i class="bi bi-arrow-repeat"></i> Convert
    </button>
    <ul class="dropdown-menu dropdown-menu-end shadow-sm">
        <li *ngFor="let opt of visibleOptions">
            <button class="dropdown-item" style="font-size:0.85rem;" type="button" (click)="openConvertModal(opt)"><i class="bi bi-chevron-right me-1"></i>{{ opt.label }}</button>
        </li>
        <li><div id="convertCustomOptions"></div></li>
    </ul>
</div>


<!-- ═══════════════════════════════════════════════════════════════════════
     Target Picker Modal
     Opened by openForRow() — used when triggered from a list page action.
     ═══════════════════════════════════════════════════════════════════════ -->
<ng-template #targetSelectModal>

    <div class="modal-header py-2">
        <h5 class="modal-title mb-0">
            <i class="bi bi-arrow-repeat text-white me-1"></i>Convert To
        </h5>
        <button type="button" class="btn-close"
                (click)="closeTargetSelector()"
                aria-label="Close">
        </button>
    </div>

    <div class="modal-body p-2">
        <div class="list-group list-group-flush">
            <button *ngFor="let opt of visibleOptions"
                    class="list-group-item list-group-item-action py-2 px-3"
                    type="button"
                    (click)="selectTarget(opt)"><i class="bi bi-chevron-right me-1 text-primary"></i>{{ opt.label }}</button>
        </div>
    </div>

</ng-template>


<!-- ═══════════════════════════════════════════════════════════════════════
     Convert Form Modal
     ═══════════════════════════════════════════════════════════════════════ -->
<ng-template #convertModal>

    <div class="modal-header py-2">
        <h5 class="modal-title mb-0">
            <i class="bi bi-arrow-repeat text-white me-1"></i>{{ selectedOption?.label }}
        </h5>
        <button type="button" class="btn-close"
                (click)="closeModal()"
                [disabled]="isSubmitting"
                aria-label="Close">
        </button>
    </div>

    <div class="modal-body">

        <!-- SUCCESS STATE -->
        <ng-container *ngIf="convertResult; else formContent">
            <div class="text-center py-4">
                <i class="bi bi-check-circle-fill text-success d-block" style="font-size:3rem;"></i>
                <p class="mb-0 text-muted" style="font-size:0.8rem;">Document created successfully</p>
                <div class="mt-3">
                    <p class="mb-0 fw-semibold" style="font-size:0.95rem;">
                        {{ targetLabel }} No. &nbsp;&mdash;&nbsp;
                        <a href="javascript:void(0)"
                           class="text-primary text-decoration-underline"
                           (click)="navigateToTarget()">
                            {{ convertResult.targetDocNo }}
                        </a>
                    </p>
                    <p class="text-muted mb-0 mt-1" style="font-size:0.85rem;">Click the document number to open it.</p>
                </div>
            </div>
        </ng-container>

        <!-- FORM STATE -->
        <ng-template #formContent>

            <!-- ── Source summary bar ───────────────────────────────────────── -->
            <div class="d-flex align-items-center gap-3 px-1 mb-3 flex-wrap">
                <div *ngIf="SourceMasterData?.CName" class="d-flex align-items-center gap-1">
                    <span class="text-muted small">Company:</span>
                    <span class="fw-semibold small">{{ SourceMasterData?.CName }}</span>
                </div>
                <div *ngIf="SourceDocNo" class="d-flex align-items-center gap-1">
                    <span class="text-muted small">Doc No:</span>
                    <span class="fw-semibold small">{{ SourceDocNo }}</span>
                </div>
                <div class="d-flex align-items-center gap-1">
                    <span class="text-muted small">Type:</span>
                    <span class="fw-semibold small">{{ SourceType }}</span>
                </div>
            </div>

            <!-- ── Detail reference table ────────────────────────────────────── -->
            <div *ngIf="SourceDetailData?.length" class="mb-3" style="overflow-x:auto; border:0.5px solid #dee2e6; border-radius:6px;">
                <table class="table table-sm table-hover mb-0" style="font-size:0.8rem; min-width:900px;">
                    <thead class="table-light">
                        <tr>
                            <th class="px-2 py-2">Sr No</th>
                            <th class="px-2 py-2">Item Name</th>
                            <th class="px-2 py-2 text-center">Unit</th>
                            <th class="px-2 py-2 text-end">Qty</th>
                            <th class="px-2 py-2 text-end">Price</th>
                            <th class="px-2 py-2 text-end">Discount</th>
                            <th class="px-2 py-2 text-end">GST%</th>
                            <th class="px-2 py-2 text-end">CGST</th>
                            <th class="px-2 py-2 text-end">SGST</th>
                            <th class="px-2 py-2 text-end">IGST</th>
                            <th class="px-2 py-2 text-end">Tax Amt</th>
                            <th class="px-2 py-2 text-end">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let row of SourceDetailData; let i = index">
                            <td class="px-2 py-1 text-muted">{{ i + 1 }}</td>
                            <td class="px-2 py-1">{{ getVal(row, 'itemDetails', 'itemName') }}</td>
                            <td class="px-2 py-1 text-center">{{ getVal(row, 'qtyType') }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'qty', 'quantity') }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'price', 'rate', 'unitPrice') }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'discount') || '—' }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'gstRate') || '—' }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'cgstAmt') || '—' }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'sgstAmt') || '—' }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'igstAmt') || '—' }}</td>
                            <td class="px-2 py-1 text-end">{{ getVal(row, 'taxAmount', 'tax') || '—' }}</td>
                            <td class="px-2 py-1 text-end fw-semibold">{{ getVal(row, 'totalAmount') }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- ── Convert options ───────────────────────────────────────────── -->
            <form [formGroup]="convertForm" autocomplete="off">
                <div class="row g-3">
                    <div [ngClass]="isQuotationTarget ? 'col-md-4' : 'col-md-6'">
                        <label class="form-label small fw-medium">{{ targetLabel }} Date</label>
                        <input type="date" class="form-control form-control-sm"
                               formControlName="overrideDate">
                    </div>
                    <!-- Valid Until — Quotation targets only -->
                    <div class="col-md-4" *ngIf="isQuotationTarget">
                        <label class="form-label small fw-medium">Valid Until <span class="text-muted fw-normal">(Optional)</span></label>
                        <input type="date" class="form-control form-control-sm"
                               formControlName="validUntil"
                               [min]="convertForm.get('overrideDate')?.value"
                               [class.is-invalid]="showValidUntilError">
                        <div class="invalid-feedback d-block" *ngIf="showValidUntilError">
                            Valid Until cannot be earlier than the {{ targetLabel }} date.
                        </div>
                    </div>
                    <div [ngClass]="isQuotationTarget ? 'col-md-4' : 'col-md-6'">
                        <label class="form-label small fw-medium">{{ targetLabel }} Remarks <span class="text-muted fw-normal">(Optional)</span></label>
                        <input type="text" class="form-control form-control-sm"
                               formControlName="overrideRemarks"
                               placeholder="Leave blank to copy source remarks">
                    </div>
                </div>
            </form>

        </ng-template>
    </div>

    <div class="modal-footer py-2" *ngIf="!convertResult">
        <button class="btn btn-sm btn-secondary" type="button"
                (click)="closeModal()" [disabled]="isSubmitting">
            Cancel
        </button>
        <button class="btn btn-sm btn-primary" type="button"
                (click)="onSubmit()" [disabled]="isSubmitting || convertForm.invalid">
            <span *ngIf="isSubmitting"
                  class="spinner-border spinner-border-sm me-1"
                  role="status" aria-hidden="true">
            </span>
            {{ isSubmitting ? 'Converting...' : 'Convert' }}
        </button>
    </div>

</ng-template>
                                                                    